import React, { useState, useEffect, useRef } from 'react';
import './Availibility.css';
import ControlsComponent from './SubComponent/Controls';
import { useSelector } from 'react-redux';
import backend from '../../../../../api';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from "notistack";
import { IconButton, FormControlLabel, Checkbox, ToggleButton, ToggleButtonGroup } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DayEditModal from './SubComponent/DayEditModal';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

function Availability() {
  const { user } = useSelector((state) => state.auth);
  const { enqueueSnackbar } = useSnackbar();
  const userId = user?._id;

  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [globalBreakTimes, setGlobalBreakTimes] = useState([]);
  const [timezone, setTimezone] = useState('UTC');
  const [dayWiseSettings, setDayWiseSettings] = useState({});
  const [selectedDates, setSelectedDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState({});
  const [slots, setSlots] = useState({});
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(false);
  const [selectedDurationFilter, setSelectedDurationFilter] = useState('all');

  const [newDurationTime, setNewDurationTime] = useState('');
  const [newDurationPrice, setNewDurationPrice] = useState('');
  const [durationError, setDurationError] = useState('');
  const [editingDurationIndex, setEditingDurationIndex] = useState(null);
  const [editingDurationTime, setEditingDurationTime] = useState('');
  const [editingDurationPrice, setEditingDurationPrice] = useState('');
  const [calendarDate, setCalendarDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const durationsRef = useRef(null);
  const [showDayEditModal, setShowDayEditModal] = useState(false);
  const [editingDay, setEditingDay] = useState(null);

  const handleAddDuration = () => {
    const dTime = parseInt(newDurationTime, 10);
    const dPrice = parseFloat(newDurationPrice);
    if (!dTime || dTime <= 0) {
      setDurationError('Please enter a valid duration in minutes.');
      return;
    }
    if (!dPrice || dPrice <= 0) {
      setDurationError('Please enter a valid price for this duration.');
      return;
    }
    const exists = selectedDurations.some((d) => d.time === dTime);
    if (exists) {
      setDurationError('This duration already exists. Edit it instead.');
      return;
    }
    const newDur = { time: dTime, price: dPrice };
    setSelectedDurations([...selectedDurations, newDur]);
    setNewDurationTime('');
    setNewDurationPrice('');
    setDurationError('');
  };

  const handleEditDuration = (index) => {
    setEditingDurationIndex(index);
    setEditingDurationTime(selectedDurations[index].time);
    setEditingDurationPrice(selectedDurations[index].price);
  };

  const handleSaveEditDuration = (index) => {
    const dTime = parseInt(editingDurationTime, 10);
    const dPrice = parseFloat(editingDurationPrice);
    if (!dTime || dTime <= 0) {
      setDurationError('Please enter a valid duration in minutes.');
      return;
    }
    if (!dPrice || dPrice <= 0) {
      setDurationError('Please enter a valid price for this duration.');
      return;
    }
    const duplicateCheck = selectedDurations.some((d, i) => d.time === dTime && i !== index);
    if (duplicateCheck) {
      setDurationError('This duration already exists.');
      return;
    }
    const updated = [...selectedDurations];
    updated[index] = { time: dTime, price: dPrice };
    setSelectedDurations(updated);
    setEditingDurationIndex(null);
    setEditingDurationTime('');
    setEditingDurationPrice('');
    setDurationError('');
  };

  const handleCancelEditDuration = () => {
    setEditingDurationIndex(null);
    setEditingDurationTime('');
    setEditingDurationPrice('');
    setDurationError('');
  };

  const handleRemoveDuration = (index) => {
    const updated = [...selectedDurations];
    updated.splice(index, 1);
    setSelectedDurations(updated);
    if (selectedDurationFilter !== 'all' && parseInt(selectedDurationFilter) === updated[index]?.time) {
      setSelectedDurationFilter('all');
    }
  };

  function formatTime12(input) {
    let h, m;
    if (input instanceof Date) {
      h = input.getHours();
      m = input.getMinutes();
    } else if (typeof input === 'string') {
      const [hs, ms = '0'] = input.split(':');
      h = Number(hs);
      m = Number(ms);
    } else {
      return '';
    }
    const h12 = (h % 12) || 12;
    const mm = String(m).padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h12}:${mm} ${ampm}`;
  }

  useEffect(() => {
    if (!userId) return;
    async function fetchUser() {
      try {
        const res = await backend.get(`/api/user/${userId}`);
        const data = res.data.data.mentorProfile;
        setStartTime(data?.availability?.timeSettings?.startTime || "09:00");
        setEndTime(data?.availability?.timeSettings?.endTime || "17:00");
        setSelectedDurations(data?.availability?.timeSettings?.durations || []);
        setBreakMinutes(data?.availability?.timeSettings?.breakMinutes || 0);
        setGlobalBreakTimes(data?.availability?.timeSettings?.breakTimes || []);
        setTimezone(data?.availability?.timeSettings?.timezone || 'UTC');
        setRepeatWeeks(data?.availability?.timeSettings?.repeatWeeks || false);
        setDayWiseSettings(data?.availability?.dayWiseSettings || {});
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const validDates = Object.keys(data?.availability?.availableSlots || {}).filter(ds => {
          const dd = new Date(ds);
          return dd >= now;
        });
        const newAvail = {};
        validDates.forEach(ds => { newAvail[ds] = data.availability.availableSlots[ds]; });
        setSelectedDates(validDates);
        setAvailableSlots(newAvail);
      } catch (err) {
        console.error("Error fetching service:", err);
      }
    }
    fetchUser();
  }, [userId]);

  useEffect(() => {
    if (!skipWeekends) return;
    setSelectedDates(prev => {
      const filtered = prev.filter(ds => !isWeekend(new Date(ds)));
      setAvailableSlots(prevSlots => {
        const updated = { ...prevSlots };
        prev.forEach(ds => {
          if (isWeekend(new Date(ds))) delete updated[ds];
        });
        return updated;
      });
      return filtered;
    });
  }, [skipWeekends]);

  function getAvailableWindows(startTime, endTime, breakTimes) {
    const windows = [];
    let current = parseTime(startTime);
    const endObj = parseTime(endTime);
    const sortedBreaks = [...breakTimes].sort((a, b) => parseTime(a.start) - parseTime(b.start));
    for (const brk of sortedBreaks) {
      const brkStart = parseTime(brk.start);
      const brkEnd = parseTime(brk.end);
      if (current < brkStart) {
        windows.push({ start: formatTime(current), end: formatTime(brkStart) });
      }
      current = brkEnd > current ? brkEnd : current;
    }
    if (current < endObj) {
      windows.push({ start: formatTime(current), end: formatTime(endObj) });
    }
    return windows;
  }

  useEffect(() => {
    if (!selectedDates.length || !selectedDurations.length) return;
    const newSlots = { ...slots };
    const newAvail = { ...availableSlots };
    selectedDates.forEach(dateStr => {
      const dayOverride = dayWiseSettings[dateStr] || {};
      const dayStart = dayOverride.startTime || startTime;
      const dayEnd = dayOverride.endTime || endTime;
      const dayBreaks = Array.isArray(dayOverride.breakTimes) ? dayOverride.breakTimes : globalBreakTimes;
      const availableWindows = getAvailableWindows(dayStart, dayEnd, dayBreaks);
      if (!newSlots[dateStr]) newSlots[dateStr] = {};
      if (!newAvail[dateStr]) newAvail[dateStr] = {};
      selectedDurations.forEach(dObj => {
        const dur = dObj.time;
        if (!newSlots[dateStr][dur]) newSlots[dateStr][dur] = [];
        if (!newAvail[dateStr][dur]) newAvail[dateStr][dur] = {};
        const slotObjs = [];
        availableWindows.forEach(win => {
          const winSlots = generateSlots(win.start, win.end, dur, breakMinutes);
          slotObjs.push(...winSlots);
        });
        newSlots[dateStr][dur] = slotObjs;
        slotObjs.forEach(sObj => {
          const key = sObj.start;
          const oldVal = availableSlots?.[dateStr]?.[dur]?.[key];
          if (oldVal === "unavailable") {
            newAvail[dateStr][dur][key] = "unavailable";
          } else {
            newAvail[dateStr][dur][key] = "available";
          }
        });
        Object.keys(newAvail[dateStr][dur]).forEach(startKey => {
          if (!slotObjs.some(s => s.start === startKey)) {
            delete newAvail[dateStr][dur][startKey];
          }
        });
      });
    });
    setSlots(newSlots);
    setAvailableSlots(newAvail);
  }, [selectedDates, selectedDurations, breakMinutes, startTime, endTime, globalBreakTimes, dayWiseSettings]);

  function generateDaysArray(year, month) {
    const result = [];
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    for (let day = first.getDate(); day <= last.getDate(); day++) {
      result.push(new Date(year, month, day));
    }
    return result;
  }

  function generateSlots(start, end, duration, gap) {
    const slotsArr = [];
    let current = parseTime(start);
    const endObj = parseTime(end);
    while (current < endObj) {
      const slotStart = formatTime(current);
      const slotEnd = addMinutesToTime(current, duration);
      if (slotEnd > endObj) break;
      slotsArr.push({ start: slotStart, end: formatTime(slotEnd) });
      current = addMinutesToTime(slotEnd, gap);
    }
    return slotsArr;
  }

  function parseTime(str) {
    if (!str) return new Date(0);
    const [h, m] = str.split(':').map(Number);
    const d = new Date();
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
  }

  function formatTime(d) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  function addMinutesToTime(d, mins) {
    const nd = new Date(d);
    nd.setMinutes(nd.getMinutes() + mins);
    return nd;
  }

  function formatDateKey(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function isWeekend(d) {
    const n = d.getDay();
    return n === 0 || n === 6;
  }

  function slotsOverlap(slotA, breakObj) {
    const [ah, am] = slotA?.start?.split(':')?.map(Number) || [];
    const [aeh, aem] = slotA?.end?.split(':')?.map(Number) || [];
    const [bh, bm] = breakObj?.start?.split(':')?.map(Number) || [];
    const [beh, bem] = breakObj?.end?.split(':')?.map(Number) || [];
    const startA = ah * 60 + (am || 0);
    const endA = aeh * 60 + (aem || 0);
    const startB = bh * 60 + (bm || 0);
    const endB = beh * 60 + (bem || 0);
    return startA < endB && endA > startB;
  }

  function groupSlotsByTimeOfDay(slotArr) {
    const morning = [];
    const afternoon = [];
    const evening = [];
    slotArr.forEach(s => {
      const h = parseInt(s.start.split(':')[0], 10);
      if (h < 12) morning.push(s);
      else if (h < 18) afternoon.push(s);
      else evening.push(s);
    });
    return { morning, afternoon, evening };
  }

  function handleDayClick(d) {
    if (d < today) return;
    if (skipWeekends && isWeekend(d)) return;
    if (!selectedDurations.length) {
      enqueueSnackbar("Please add a duration & price first.", { variant: "warning" });
      durationsRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const dStr = formatDateKey(d);
    const found = selectedDates.includes(dStr);
    if (!found) {
      const newDates = [dStr];
      const newSettings = { [dStr]: { startTime, endTime, breakTimes: globalBreakTimes || [] } };
      const newAvail = { [dStr]: { ...availableSlots[dStr] } };
      if (repeatWeeks) {
        const weeksToAdd = 12; // Repeat for 12 weeks
        for (let i = 1; i < weeksToAdd; i++) {
          const futureDate = new Date(d);
          futureDate.setDate(d.getDate() + i * 7);
          if (futureDate < today) continue;
          const futureDStr = formatDateKey(futureDate);
          newDates.push(futureDStr);
          newSettings[futureDStr] = { startTime, endTime, breakTimes: globalBreakTimes || [] };
          newAvail[futureDStr] = { ...availableSlots[dStr] };
        }
      }
      setSelectedDates(prev => [...new Set([...prev, ...newDates])]);
      setDayWiseSettings(prev => ({ ...prev, ...newSettings }));
      setAvailableSlots(prev => ({ ...prev, ...newAvail }));
    } else {
      const datesToRemove = [dStr];
      if (repeatWeeks) {
        const weeksToCheck = 12;
        for (let i = 1; i < weeksToCheck; i++) {
          const futureDate = new Date(d);
          futureDate.setDate(d.getDate() + i * 7);
          const futureDStr = formatDateKey(futureDate);
          if (selectedDates.includes(futureDStr)) {
            datesToRemove.push(futureDStr);
          }
        }
      }
      setSelectedDates(prev => prev.filter(x => !datesToRemove.includes(x)));
      setAvailableSlots(prev => {
        const copy = { ...prev };
        datesToRemove.forEach(ds => delete copy[ds]);
        return copy;
      });
      setSlots(prev => {
        const copy = { ...prev };
        datesToRemove.forEach(ds => delete copy[ds]);
        return copy;
      });
      setDayWiseSettings(prev => {
        const copy = { ...prev };
        datesToRemove.forEach(ds => delete copy[ds]);
        return copy;
      });
    }
  }

  function toggleAvailability(dateStr, dur, sObj) {
    if (!dateStr || !dur || !sObj?.start) return;
    setAvailableSlots(prev => {
      const updated = { ...prev };
      if (!updated[dateStr]) updated[dateStr] = {};
      if (!updated[dateStr][dur]) updated[dateStr][dur] = {};
      const oldVal = updated[dateStr][dur][sObj.start];
      const newVal = (oldVal === "available") ? "unavailable" : "available";
      updated[dateStr][dur][sObj.start] = newVal;
      if (repeatWeeks && newVal === "available") {
        const baseDate = new Date(dateStr);
        const weeksToAdd = 12;
        for (let i = 1; i < weeksToAdd; i++) {
          const futureDate = new Date(baseDate);
          futureDate.setDate(baseDate.getDate() + i * 7);
          const futureDStr = formatDateKey(futureDate);
          if (selectedDates.includes(futureDStr)) {
            if (!updated[futureDStr]) updated[futureDStr] = {};
            if (!updated[futureDStr][dur]) updated[futureDStr][dur] = {};
            updated[futureDStr][dur][sObj.start] = newVal;
          }
        }
      }
      if (newVal === "available") {
        Object.keys(updated[dateStr]).forEach(otherDur => {
          if (parseInt(otherDur, 10) === parseInt(dur, 10)) return;
          const theseSlots = slots?.[dateStr]?.[otherDur] || [];
          theseSlots.forEach(eachSlot => {
            if (slotsOverlap(sObj, { start: eachSlot.start, end: eachSlot.end })) {
              if (updated[dateStr][otherDur][eachSlot.start] === "available") {
                updated[dateStr][otherDur][eachSlot.start] = "unavailable";
                if (repeatWeeks) {
                  const baseDate = new Date(dateStr);
                  const weeksToAdd = 12;
                  for (let i = 1; i < weeksToAdd; i++) {
                    const futureDate = new Date(baseDate);
                    futureDate.setDate(baseDate.getDate() + i * 7);
                    const futureDStr = formatDateKey(futureDate);
                    if (selectedDates.includes(futureDStr)) {
                      if (!updated[futureDStr]) updated[futureDStr] = {};
                      if (!updated[futureDStr][otherDur]) updated[futureDStr][otherDur] = {};
                      if (updated[futureDStr][otherDur][eachSlot.start] === "available") {
                        updated[futureDStr][otherDur][eachSlot.start] = "unavailable";
                      }
                    }
                  }
                }
              }
            }
          });
        });
      }
      return updated;
    });
  }

  function handleNextMonth() {
    setCalendarDate(prev => {
      let m = prev.getMonth() + 1;
      let y = prev.getFullYear();
      if (m > 11) {
        m = 0;
        y++;
      }
      return new Date(y, m, 1);
    });
  }

  function handlePrevMonth() {
    setCalendarDate(prev => {
      let m = prev.getMonth() - 1;
      let y = prev.getFullYear();
      if (m < 0) {
        m = 11;
        y--;
      }
      return new Date(y, m, 1);
    });
  }

  function handleGoToToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCalendarDate(d);
    const dStr = formatDateKey(d);
    if (!selectedDates.includes(dStr) && selectedDurations.length) {
      setSelectedDates([...selectedDates, dStr]);
      setDayWiseSettings(prev => ({
        ...prev,
        [dStr]: {
          startTime,
          endTime,
          breakTimes: globalBreakTimes
        }
      }));
    }
  }

  function handleEditDayClick(dStr) {
    setEditingDay(dStr);
    setShowDayEditModal(true);
  }

  function handleSaveDaySettings(dStr, updatedObj) {
    setDayWiseSettings(prev => {
      const newSettings = { ...prev, [dStr]: updatedObj };
      if (repeatWeeks) {
        const baseDate = new Date(dStr);
        const weeksToAdd = 12;
        for (let i = 1; i < weeksToAdd; i++) {
          const futureDate = new Date(baseDate);
          futureDate.setDate(baseDate.getDate() + i * 7);
          const futureDStr = formatDateKey(futureDate);
          if (selectedDates.includes(futureDStr)) {
            newSettings[futureDStr] = { ...updatedObj };
          }
        }
      }
      return newSettings;
    });
    setShowDayEditModal(false);
    setEditingDay(null);
  }

  async function handleSave() {
    const errors = [];
    if (!selectedDurations?.length) {
      errors.push("At least one Duration");
    }
    if (errors.length) {
      enqueueSnackbar("Please fill required fields: " + errors.join(", "), { variant: "error" });
      return;
    }
    const body = {
      mentorProfile: {
        ...user?.mentorProfile,
        availability: {
          timeSettings: {
            startTime,
            endTime,
            durations: selectedDurations,
            breakMinutes,
            breakTimes: globalBreakTimes,
            timezone,
            repeatWeeks
          },
          dayWiseSettings,
          availableSlots,
        }
      }
    };
    try {
      setLoading(true);
      let resp;
      if (userId) {
        resp = await backend.put(`/api/user/${userId}`, body);
      }
      if (resp?.status === 200) {
        enqueueSnackbar(`Availability updated successfully!`, { variant: "success" });
      } else {
        enqueueSnackbar("Error saving availability: " + resp?.data?.error, { variant: "error" });
      }
    } catch (err) {
      console.error("Save error:", err);
      enqueueSnackbar("Error: " + err?.response?.data?.error || err.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  const handleDurationFilterChange = (event, newDuration) => {
    if (newDuration) {
      setSelectedDurationFilter(newDuration);
    }
  };

  const viewYear = calendarDate.getFullYear();
  const viewMonth = calendarDate.getMonth();
  const daysArray = generateDaysArray(viewYear, viewMonth);
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const showAvailability = true;

  return (
    <div className={'hero'}>
      <div className={'header'}>
        <div>
          <h3 className={'title3'}>Availability</h3>
          <h5 className={'text'}>Set your availability here.</h5>
        </div>
        <div></div>
      </div>

      <div className='availibility-body' >
        {/* <Grid item xs={12} md={6}>
          <Box className="control-group">
            <Typography variant="subtitle1" gutterBottom>
              Durations & Price
            </Typography>
            <Box sx={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
              <TextField
                label="Duration (min)"
                variant="outlined"
                type="number"
                size="small"
                value={newDurationTime}
                onChange={(e) => setNewDurationTime(e.target.value)}
              />
              <TextField
                label="Price ($)"
                variant="outlined"
                type="number"
                size="small"
                value={newDurationPrice}
                onChange={(e) => setNewDurationPrice(e.target.value)}
              />
              <button className='submitButton' onClick={handleAddDuration}>
                Add
              </button>
            </Box>
            {durationError && (
              <Typography variant="body2" color="error" gutterBottom>
                {durationError}
              </Typography>
            )}
            <List>
              {selectedDurations?.map((dur, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  {editingDurationIndex === index ? (
                    <>
                      <TextField
                        variant="standard"
                        type="number"
                        value={editingDurationTime}
                        onChange={(e) => setEditingDurationTime(e.target.value)}
                        size="small"
                        sx={{ width: '60px', marginRight: '10px' }}
                      />
                      <TextField
                        variant="standard"
                        type="number"
                        value={editingDurationPrice}
                        onChange={(e) => setEditingDurationPrice(e.target.value)}
                        size="small"
                        sx={{ width: '60px', marginRight: '10px' }}
                      />
                      <Tooltip title="Save">
                        <IconButton
                          edge="end"
                          onClick={() => handleSaveEditDuration(index)}
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          edge="end"
                          onClick={handleCancelEditDuration}
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <ListItemText
                        primary={`${dur.time} min — $${dur.price.toFixed(2)}`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton
                            edge="end"
                            onClick={() => handleEditDuration(index)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveDuration(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid> */}

        {showAvailability && (
          <>
            {/* <div className="calendly-controls" ref={durationsRef}>
            <ControlsComponent
              selectedDurations={selectedDurations}
              setSelectedDurations={setSelectedDurations}
              breakMinutes={breakMinutes}
              setBreakMinutes={setBreakMinutes}
              breakTimes={globalBreakTimes}
              setBreakTimes={setGlobalBreakTimes}
              timeOptions={generateTimeOptions()}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              timezone={timezone}
              setTimezone={setTimezone}
            />
          </div> */}

            <div style={{ marginTop: '10px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipWeekends}
                    onChange={() => setSkipWeekends(!skipWeekends)}
                    sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' } }}
                  />
                }
                label={<Typography className='placeholderText'>Skip Weekend Days</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={repeatWeeks}
                    onChange={() => setRepeatWeeks(!repeatWeeks)}
                    sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' } }}
                  />
                }
                label={<Typography className='placeholderText'>Repeat Slots for Upcoming Weeks</Typography>}
              />
            </div>

            <div className="calendar-nav" style={{ marginTop: '20px' }}>
              <button onClick={handlePrevMonth} className="calendar-arrow">❮</button>
              <h3 className="calendar-month">
                {calendarDate.toLocaleString('default', { month: 'long' })} {viewYear}
              </h3>
              <button onClick={handleNextMonth} className="calendar-arrow">❯</button>
              {/* <button onClick={handleGoToToday} className="today-btn">Today</button> */}
            </div>

            <br />

            <div className="calendar-slot-container">
              <div className="calendar-column">
                <div className="weekday-row">
                  <div>Sun</div><div>Mon</div><div>Tue</div>
                  <div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
                </div>
                <div className="days-grid">
                  {Array(firstDayOfMonth.getDay()).fill(null).map((_, i) => (
                    <div key={`blank-${i}`} className="blank-day"></div>
                  ))}
                  {daysArray.map(dateObj => {
                    const dStr = formatDateKey(dateObj);
                    const isSelected = selectedDates.includes(dStr);
                    const isPast = dateObj < today;
                    const weekend = isWeekend(dateObj);
                    const skipDisabled = skipWeekends && weekend;
                    let dayClass = "day-cell";
                    if (isSelected) dayClass += " selected";
                    if (isPast) dayClass += " past-day";
                    if (skipDisabled) dayClass += " weekend-disabled";
                    return (
                      <div
                        key={dStr}
                        className={dayClass}
                        onClick={() => handleDayClick(dateObj)}
                        style={{ position: 'relative' }}
                      >
                        {dateObj.getDate()}
                        {/* {(isSelected && !isPast && !skipDisabled) && (
                        <IconButton
                          size="small"
                          style={{
                            position: 'absolute',
                            top: '-1rem', left: '1.5rem',
                            backgroundColor: 'transparent',
                            zIndex: 2
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditDayClick(dStr);
                          }}
                        >
                          <EditIcon fontSize="inherit" style={{ color: 'black' }} />
                        </IconButton>
                      )} */}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <Box sx={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
              <ToggleButtonGroup
                value={selectedDurationFilter}
                exclusive
                onChange={handleDurationFilterChange}
                sx={{
                  '& .MuiToggleButton-root': {
                    backgroundColor: '#f7f8fa',
                    color: '#333',
                    borderRadius: '8px',
                    margin: '0 5px',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    border: '1px solid #ccc',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--primary-color)',
                      color: '#fff',
                    }
                  },
                }}
              >
                <ToggleButton value="all">All Durations</ToggleButton>
                {selectedDurations.map((dur) => (
                  <ToggleButton key={dur.time} value={dur.time.toString()}>
                    {dur.time} min
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {(selectedDates.length > 0 && selectedDurations.length > 0) && (
              <div className="multiple-calendars" style={{ marginTop: 30 }}>
                {[...selectedDates].sort((a, b) => new Date(a) - new Date(b)).map(dateStr => {
                  const [y, m, d] = dateStr.split('-').map(Number);
                  const localDate = new Date(y, m - 1, d);
                  const dateLabel = localDate.toDateString().replace(/^(\w+) (\w+) (\d+) (\d+)$/, "$1, $2 $3, $4");
                  return (
                    <div key={dateStr} className="date-block">
                      <h3 style={{ marginBottom: 10 }}>{dateLabel}</h3>
                      {selectedDurations.filter(dObj => selectedDurationFilter === 'all' || dObj.time.toString() === selectedDurationFilter).map(dObj => {
                        const dur = dObj?.time;
                        if (!dur) return null;
                        const slotArr = slots?.[dateStr]?.[dur] || [];
                        const { morning, afternoon, evening } = groupSlotsByTimeOfDay(slotArr);
                        return (
                          <div key={dur} className="duration-calendar">
                            <h3 className="slots-title">
                              {dObj.time} min slots (${dObj.price.toFixed(2)}) for {dateLabel}
                            </h3>
                            {morning.length > 0 && (
                              <>
                                <h4 className="time-section-title">Morning</h4>
                                <div className="slots-grid">
                                  {morning.map((slotObj, i) => {
                                    const val = availableSlots?.[dateStr]?.[dur]?.[slotObj.start];
                                    return (
                                      <div
                                        key={slotObj.start + i}
                                        className={"slot " + (val === "available" ? "available" : "unavailable")}
                                        onClick={() => toggleAvailability(dateStr, dur, slotObj)}
                                      >
                                        {formatTime12(slotObj.start)} - {formatTime12(slotObj.end)}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            {afternoon.length > 0 && (
                              <>
                                <h4 className="time-section-title">Afternoon</h4>
                                <div className="slots-grid">
                                  {afternoon.map((slotObj, i) => {
                                    const val = availableSlots?.[dateStr]?.[dur]?.[slotObj.start];
                                    return (
                                      <div
                                        key={slotObj.start + i}
                                        className={"slot " + (val === "available" ? "available" : "unavailable")}
                                        onClick={() => toggleAvailability(dateStr, dur, slotObj)}
                                      >
                                        {formatTime12(slotObj.start)} - {formatTime12(slotObj.end)}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            {evening.length > 0 && (
                              <>
                                <h4 className="time-section-title">Evening</h4>
                                <div className="slots-grid">
                                  {evening.map((slotObj, i) => {
                                    const val = availableSlots?.[dateStr]?.[dur]?.[slotObj.start];
                                    return (
                                      <div
                                        key={slotObj.start + i}
                                        className={"slot " + (val === "available" ? "available" : "unavailable")}
                                        onClick={() => toggleAvailability(dateStr, dur, slotObj)}
                                      >
                                        {formatTime12(slotObj.start)} - {formatTime12(slotObj.end)}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                            {morning.length === 0 && afternoon.length === 0 && evening.length === 0 && (
                              <div className="no-slots-message">
                                <p>No slots generated for this duration.</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className='add-service-footer'>
          <button
            onClick={handleSave}
            disabled={loading}
            className="save-button"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

        {showDayEditModal && (
          <DayEditModal
            dateStr={editingDay}
            existingOverrides={dayWiseSettings?.[editingDay]}
            globalStart={startTime}
            globalEnd={endTime}
            globalBreaks={globalBreakTimes}
            onClose={() => { setShowDayEditModal(false); setEditingDay(null); }}
            onSave={handleSaveDaySettings}
          />
        )}
      </div>
    </div>
  );

  function generateTimeOptions() {
    const arr = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        arr.push(`${hh}:${mm}`);
      }
    }
    return arr;
  }
}

export default Availability;